import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
  CheckoutDto,
  InstoreCheckoutDto,
  VerifyPaymentDto,
  CheckoutResponseDto,
  CheckoutState,
  ApiResponse,
  CalculateBillRequest,
  CalculateBillResponse,
} from "./types";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_PAYMENT_APP_BACKEND_PREFIX,
]
  .filter(Boolean)
  .join("");

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const raw = localStorage.getItem("access_token") || getCookie("access_token");
    const token = raw && raw !== "undefined" && raw !== "null" && raw.length > 20 ? raw : null;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const normalizeApiError = (error: any, fallback: string): string => {
  const raw = error?.response?.data?.message ?? error?.message;
  if (Array.isArray(raw)) {
    return raw.map(String).filter(Boolean).join(". ") || fallback;
  }
  if (typeof raw === "string" && raw.trim()) return raw;
  return fallback;
};

export const processCheckout = createAsyncThunk(
  "checkout/processCheckout",
  async (data: CheckoutDto, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<CheckoutResponseDto>>(
        "/checkout",
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(normalizeApiError(error, "Checkout failed"));
    }
  },
);

export const processInstoreCheckout = createAsyncThunk(
  "checkout/processInstoreCheckout",
  async (data: InstoreCheckoutDto, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<CheckoutResponseDto>>(
        "/checkout/instore",
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Instore checkout failed",
      );
    }
  },
);

export const calculateBill = createAsyncThunk(
  "checkout/calculateBill",
  async (data: CalculateBillRequest, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<CalculateBillResponse>>(
        "/payments/calculate-bill",
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to calculate bill",
      );
    }
  },
);

export const verifyPayment = createAsyncThunk(
  "checkout/verifyPayment",
  async (data: VerifyPaymentDto, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<any>>(
        "/checkout/verify-payment",
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Payment verification failed",
      );
    }
  },
);

const initialState: CheckoutState = {
  checkoutResult: null,
  paymentVerification: null,
  billDetails: null,
  loading: false,
  error: null,
  success: false,
  message: null,
};

const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    clearCheckoutState: (state) => {
      state.checkoutResult = null;
      state.paymentVerification = null;
      state.billDetails = null;
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = null;
    },
    clearCheckoutError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Process Checkout
      .addCase(processCheckout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processCheckout.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.checkoutResult = action.payload.data;
        state.message = action.payload.message || "Checkout processed";
      })
      .addCase(processCheckout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Instore Checkout
      .addCase(processInstoreCheckout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processInstoreCheckout.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.checkoutResult = action.payload.data;
        state.message = action.payload.message || "Instore checkout processed";
      })
      .addCase(processInstoreCheckout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Calculate Bill
      .addCase(calculateBill.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(calculateBill.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.billDetails = action.payload.data;
      })
      .addCase(calculateBill.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Verify Payment
      .addCase(verifyPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.paymentVerification = action.payload.data;
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCheckoutState, clearCheckoutError } = checkoutSlice.actions;
export default checkoutSlice.reducer;
